import { z } from 'zod';
import { LEAD_SOURCES } from './channels';
import { LEAD_STATUSES, objectIdSchema as objectId, workingHoursSchema } from './schemas';

/**
 * Smart lead operations (Phase 2, M4): follow-ups, workflow rules, response-time
 * alerts, internal notifications. Shared by the API and the web app.
 */

// ---------------------------------------------------------------------------
// Time zones and business hours (DST-safe, no external library)
// ---------------------------------------------------------------------------

export interface BusinessHours {
  enabled: boolean;
  /** 0 = Sunday … 6 = Saturday. */
  days: number[];
  /** HH:MM, local to the workspace time zone. An end before the start means an overnight shift. */
  start: string;
  end: string;
}

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number;
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string) {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      weekday: 'short',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/** Wall-clock parts of an instant in a time zone. */
export function zonedParts(date: Date, timeZone: string): ZonedParts {
  const parts = Object.fromEntries(formatter(timeZone).formatToParts(date).map((p) => [p.type, p.value]));
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
    weekday: WEEKDAYS[parts.weekday as string] ?? 0,
  };
}

function offsetMs(date: Date, timeZone: string) {
  const p = zonedParts(date, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(date.getTime() / 1000) * 1000;
}

/**
 * The instant at which the wall clock in `timeZone` shows the given local time.
 * A time skipped by a DST jump resolves to the moment after the jump; a repeated
 * time resolves to its first occurrence.
 */
export function zonedTimeToUtc(year: number, month: number, day: number, hour: number, minute: number, timeZone: string): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  // The offsets in force half a day either side cover any single transition.
  const before = guess - offsetMs(new Date(guess - 43_200_000), timeZone);
  const after = guess - offsetMs(new Date(guess + 43_200_000), timeZone);
  const shows = (t: number) => {
    const p = zonedParts(new Date(t), timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) === guess;
  };
  const valid = [before, after].filter(shows);
  if (valid.length) return new Date(Math.min(...valid));
  // Skipped by a forward jump: the pre-jump offset lands just after the jump.
  return new Date(before);
}

/** Parses "YYYY-MM-DDTHH:MM" as local time in the workspace zone. */
export function localInputToUtc(value: string, timeZone: string): Date | null {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  return zonedTimeToUtc(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5]), timeZone);
}

/** Formats an instant as "YYYY-MM-DDTHH:MM" in the workspace zone (for datetime-local inputs). */
export function utcToLocalInput(date: Date, timeZone: string): string {
  const p = zonedParts(date, timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** "Wed 28 Oct, 09:07" in a time zone, for notification text. */
export function formatLocal(date: Date, timeZone: string): string {
  // Built from parts: ICU versions disagree on punctuation ("Wed 28 Oct" vs "Wed, 28 Oct").
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(date)
      .map((x) => [x.type, x.value]),
  );
  return `${p.weekday} ${p.day} ${p.month}, ${p.hour}:${p.minute}`;
}

/** Start and end of the local calendar day containing `date` (23 or 25 hours long on DST days). */
export function localDayBounds(date: Date, timeZone: string): { start: Date; end: Date } {
  const p = zonedParts(date, timeZone);
  const next = new Date(Date.UTC(p.year, p.month - 1, p.day + 1));
  return {
    start: zonedTimeToUtc(p.year, p.month, p.day, 0, 0, timeZone),
    end: zonedTimeToUtc(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate(), 0, 0, timeZone),
  };
}

const minutesOf = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** Business-hour windows (UTC) for each local day that touches [from, to]. */
function windows(from: Date, to: Date, hours: BusinessHours, timeZone: string): [number, number][] {
  const out: [number, number][] = [];
  const startLocal = zonedParts(new Date(from.getTime() - 86400_000), timeZone);
  const s = minutesOf(hours.start);
  const e = minutesOf(hours.end);
  for (let i = 0; i < 400; i++) {
    const day = new Date(Date.UTC(startLocal.year, startLocal.month - 1, startLocal.day + i));
    const y = day.getUTCFullYear();
    const mo = day.getUTCMonth() + 1;
    const d = day.getUTCDate();
    const open = zonedTimeToUtc(y, mo, d, Math.floor(s / 60), s % 60, timeZone).getTime();
    if (open > to.getTime()) break;
    if (!hours.days.includes(day.getUTCDay())) continue;
    let close: number;
    if (e > s) close = zonedTimeToUtc(y, mo, d, Math.floor(e / 60), e % 60, timeZone).getTime();
    else {
      const nextDay = new Date(Date.UTC(y, mo - 1, d + 1));
      close = zonedTimeToUtc(nextDay.getUTCFullYear(), nextDay.getUTCMonth() + 1, nextDay.getUTCDate(), Math.floor(e / 60), e % 60, timeZone).getTime();
    }
    out.push([open, close]);
  }
  return out;
}

/** Whether an instant falls inside business hours. Disabled business hours = always open. */
export function withinBusinessHours(date: Date, hours: BusinessHours, timeZone: string): boolean {
  if (!hours.enabled) return true;
  const t = date.getTime();
  return windows(date, date, hours, timeZone).some(([a, b]) => t >= a && t < b);
}

/**
 * Minutes between two instants that fall inside business hours (calendar
 * minutes when business hours are off). Real elapsed minutes: a DST change
 * inside a window lengthens or shortens it accordingly.
 */
export function businessMinutesBetween(from: Date, to: Date, hours: BusinessHours, timeZone: string): number {
  if (to <= from) return 0;
  if (!hours.enabled) return (to.getTime() - from.getTime()) / 60000;
  let total = 0;
  for (const [a, b] of windows(from, to, hours, timeZone)) {
    const start = Math.max(a, from.getTime());
    const end = Math.min(b, to.getTime());
    if (end > start) total += end - start;
  }
  return total / 60000;
}

// ---------------------------------------------------------------------------
// Workspace settings
// ---------------------------------------------------------------------------

export const DEFAULT_BUSINESS_HOURS: BusinessHours = { enabled: true, days: [1, 2, 3, 4, 5], start: '09:00', end: '18:00' };

export const operationsSettingsSchema = z.object({
  businessHours: workingHoursSchema,
  response: z.object({
    /** Watch for inquiries waiting too long for a human reply. */
    enabled: z.boolean(),
    thresholdMinutes: z.number().int().min(1).max(24 * 60),
    /** Count only minutes inside business hours. */
    businessHoursOnly: z.boolean(),
    markNeedsAttention: z.boolean(),
    notifyAssignee: z.boolean(),
    /** When nobody is assigned, notify owners and admins. */
    notifyAdminsWhenUnassigned: z.boolean(),
    /** Second alert to owners and admins after this many (business) minutes. Null = no escalation. */
    escalateAfterMinutes: z.number().int().min(1).max(7 * 24 * 60).nullable(),
  }),
  followUps: z.object({
    /** In-app reminder this many minutes before a follow-up is due (0 = only when due). */
    reminderMinutesBefore: z.number().int().min(0).max(24 * 60),
  }),
});
export type OperationsSettings = z.infer<typeof operationsSettingsSchema>;

export const DEFAULT_OPERATIONS_SETTINGS: OperationsSettings = {
  businessHours: DEFAULT_BUSINESS_HOURS,
  response: {
    enabled: false,
    thresholdMinutes: 15,
    businessHoursOnly: true,
    markNeedsAttention: true,
    notifyAssignee: true,
    notifyAdminsWhenUnassigned: true,
    escalateAfterMinutes: null,
  },
  followUps: { reminderMinutesBefore: 15 },
};

export interface OperationsSettingsDto extends OperationsSettings {
  timezone: string;
}

// ---------------------------------------------------------------------------
// Priority
// ---------------------------------------------------------------------------

export const LEAD_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type LeadPriority = (typeof LEAD_PRIORITIES)[number];
export const LEAD_PRIORITY_LABELS: Record<LeadPriority, string> = { low: 'Low', normal: 'Normal', high: 'High', urgent: 'Urgent' };

// ---------------------------------------------------------------------------
// Follow-ups
// ---------------------------------------------------------------------------

export const FOLLOW_UP_STATUSES = ['pending', 'completed', 'cancelled'] as const;
/** "overdue" is derived: pending and past its due time. */
export type FollowUpStatus = (typeof FOLLOW_UP_STATUSES)[number] | 'overdue';
export const FOLLOW_UP_STATUS_LABELS: Record<FollowUpStatus, string> = { pending: 'Pending', overdue: 'Overdue', completed: 'Completed', cancelled: 'Cancelled' };

export const FOLLOW_UP_BUCKETS = ['open', 'due_today', 'upcoming', 'overdue', 'completed', 'cancelled'] as const;
export type FollowUpBucket = (typeof FOLLOW_UP_BUCKETS)[number];

export const createFollowUpSchema = z
  .object({
    leadId: objectId.optional(),
    customerId: objectId.optional(),
    title: z.string().trim().min(1).max(200),
    note: z.string().trim().max(2000).optional(),
    dueAt: z.string().datetime({ offset: true }),
    /** Defaults to the creator. */
    assigneeId: objectId.nullable().optional(),
  })
  .refine((v) => v.leadId || v.customerId, { message: 'Link the follow-up to an inquiry or a customer.' });

export const updateFollowUpSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  dueAt: z.string().datetime({ offset: true }).optional(),
  assigneeId: objectId.nullable().optional(),
  status: z.enum(FOLLOW_UP_STATUSES).optional(),
});

export const listFollowUpsQuerySchema = z.object({
  view: z.enum(['mine', 'all']).default('mine'),
  bucket: z.enum(FOLLOW_UP_BUCKETS).default('open'),
  q: z.string().trim().max(100).optional(),
  assigneeId: objectId.optional(),
  leadId: objectId.optional(),
  customerId: objectId.optional(),
  page: z.coerce.number().int().min(1).max(1000).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export interface FollowUpHistoryDto {
  at: string;
  by: { id: string; name: string } | null;
  action: 'created' | 'rescheduled' | 'reassigned' | 'completed' | 'cancelled' | 'reopened' | 'edited';
  from: string | null;
  to: string | null;
}

export interface FollowUpDto {
  id: string;
  title: string;
  note: string | null;
  dueAt: string;
  status: FollowUpStatus;
  lead: { id: string; subject: string | null; source: string } | null;
  customer: { id: string; displayName: string };
  assignee: { id: string; name: string } | null;
  createdBy: { id: string; name: string } | null;
  createdByRule: { id: string; name: string } | null;
  completedBy: { id: string; name: string } | null;
  completedAt: string | null;
  createdAt: string;
  history: FollowUpHistoryDto[];
  /** The current member may edit, complete or reassign it. */
  canManage: boolean;
}

export interface FollowUpListDto {
  items: FollowUpDto[];
  total: number;
  page: number;
  pages: number;
  counts: Record<'overdue' | 'due_today' | 'upcoming', number>;
  timezone: string;
}

// ---------------------------------------------------------------------------
// Workflow rules
// ---------------------------------------------------------------------------

export const WORKFLOW_TRIGGERS = ['inquiry_created', 'customer_message', 'response_overdue'] as const;
export type WorkflowTrigger = (typeof WORKFLOW_TRIGGERS)[number];
export const WORKFLOW_TRIGGER_LABELS: Record<WorkflowTrigger, string> = {
  inquiry_created: 'A new inquiry arrives',
  customer_message: 'A customer writes again in an open conversation',
  response_overdue: 'An inquiry passes the response-time threshold',
};

export const CUSTOMER_TYPES = ['new', 'returning', 'possible_match'] as const;
export const CUSTOMER_TYPE_LABELS: Record<(typeof CUSTOMER_TYPES)[number], string> = {
  new: 'New customer',
  returning: 'Returning customer (confirmed match)',
  possible_match: 'Possible returning customer (unconfirmed)',
};

const list = <T extends z.ZodTypeAny>(item: T) => z.array(item).min(1, 'Choose at least one value.').max(50);

export const workflowConditionSchema = z.discriminatedUnion('field', [
  z.object({ field: z.literal('source'), op: z.enum(['in', 'not_in']), value: list(z.enum(LEAD_SOURCES)) }),
  z.object({ field: z.literal('connection'), op: z.enum(['in', 'not_in']), value: list(objectId) }),
  z.object({ field: z.literal('kind'), op: z.literal('is'), value: z.enum(['conversation', 'submission']) }),
  z.object({ field: z.literal('status'), op: z.enum(['in', 'not_in']), value: list(z.enum(LEAD_STATUSES)) }),
  z.object({ field: z.literal('assigned'), op: z.literal('is'), value: z.boolean() }),
  z.object({ field: z.literal('customer_type'), op: z.enum(['in', 'not_in']), value: list(z.enum(CUSTOMER_TYPES)) }),
  z.object({ field: z.literal('business_hours'), op: z.literal('is'), value: z.enum(['within', 'outside']) }),
  z.object({ field: z.literal('has_email'), op: z.literal('is'), value: z.boolean() }),
  z.object({ field: z.literal('has_phone'), op: z.literal('is'), value: z.boolean() }),
  z.object({ field: z.literal('customer_has_owner'), op: z.literal('is'), value: z.boolean() }),
  z.object({ field: z.literal('priority'), op: z.enum(['in', 'not_in']), value: list(z.enum(LEAD_PRIORITIES)) }),
]);
export type WorkflowCondition = z.infer<typeof workflowConditionSchema>;
export type WorkflowConditionField = WorkflowCondition['field'];

export const WORKFLOW_CONDITION_LABELS: Record<WorkflowConditionField, string> = {
  source: 'Source',
  connection: 'Channel',
  kind: 'Inquiry type',
  status: 'Status',
  assigned: 'Has an assignee',
  customer_type: 'Customer',
  business_hours: 'Business hours',
  has_email: 'Has an email address',
  has_phone: 'Has a phone number',
  customer_has_owner: 'Customer already has an owner',
  priority: 'Priority',
};

export const workflowActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('assign'),
    /** previous_owner keeps a returning customer with their last assignee when they are eligible. */
    mode: z.enum(['specific', 'round_robin', 'previous_owner']),
    userIds: z.array(objectId).min(1, 'Choose at least one team member.').max(100),
    /** previous_owner only: what to do when there is no eligible previous owner. */
    fallback: z.enum(['round_robin', 'none']).default('round_robin'),
  }),
  z.object({ type: z.literal('set_priority'), priority: z.enum(LEAD_PRIORITIES) }),
  z.object({ type: z.literal('mark_needs_attention') }),
  z.object({
    type: z.literal('create_follow_up'),
    title: z.string().trim().min(1).max(200),
    dueInMinutes: z.number().int().min(1).max(60 * 24 * 30),
    assignTo: z.enum(['assignee', 'specific']),
    userId: objectId.optional(),
  }),
  z.object({
    type: z.literal('notify'),
    recipients: z.enum(['assignee', 'admins', 'specific']),
    userIds: z.array(objectId).max(50).default([]),
    message: z.string().trim().max(300).default(''),
  }),
]);
export type WorkflowAction = z.infer<typeof workflowActionSchema>;
export type WorkflowActionType = WorkflowAction['type'];

export const WORKFLOW_ACTION_LABELS: Record<WorkflowActionType, string> = {
  assign: 'Assign',
  set_priority: 'Set priority',
  mark_needs_attention: 'Mark as needing attention',
  create_follow_up: 'Create a follow-up',
  notify: 'Send an internal notification',
};

export const workflowRuleSchema = z
  .object({
    type: z.literal('workflow'),
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).default(''),
    enabled: z.boolean(),
    trigger: z.enum(WORKFLOW_TRIGGERS),
    conditions: z.array(workflowConditionSchema).max(20),
    actions: z.array(workflowActionSchema).min(1).max(10),
    /** Lower runs first. */
    order: z.number().int().min(0).max(1000).default(100),
    /** Later workflow rules are not evaluated for this event once this one matches. */
    stopAfterMatch: z.boolean().default(false),
  })
  .superRefine((r, ctx) => {
    r.actions.forEach((a, i) => {
      if (a.type === 'create_follow_up' && a.assignTo === 'specific' && !a.userId) ctx.addIssue({ code: 'custom', path: ['actions', i, 'userId'], message: 'Choose who gets the follow-up.' });
      if (a.type === 'notify' && a.recipients === 'specific' && !a.userIds.length) ctx.addIssue({ code: 'custom', path: ['actions', i, 'userIds'], message: 'Choose who is notified.' });
      if (a.type === 'assign' && a.mode === 'specific' && a.userIds.length !== 1) ctx.addIssue({ code: 'custom', path: ['actions', i, 'userIds'], message: 'Choose one team member.' });
    });
    if (r.actions.filter((a) => a.type === 'assign').length > 1) ctx.addIssue({ code: 'custom', path: ['actions'], message: 'Use at most one assign action per rule.' });
  });
export type WorkflowRuleInput = z.input<typeof workflowRuleSchema>;

/** Test a saved rule, or an unsaved draft, against a real inquiry without changing anything. */
export const workflowTestSchema = z.object({
  leadId: objectId,
  ruleId: objectId.optional(),
  rule: z.unknown().optional(),
});

export interface WorkflowCheckDto {
  label: string;
  passed: boolean;
  detail: string;
}

export interface WorkflowActionOutcomeDto {
  type: WorkflowActionType;
  outcome: 'done' | 'skipped' | 'failed' | 'would_do' | 'would_skip';
  detail: string;
}

export interface WorkflowTestResultDto {
  matched: boolean;
  checks: WorkflowCheckDto[];
  actions: WorkflowActionOutcomeDto[];
  summary: string;
}

export interface AutomationExecutionDto {
  id: string;
  ruleId: string;
  ruleName: string;
  trigger: WorkflowTrigger;
  lead: { id: string; customerName: string } | null;
  status: 'running' | 'succeeded' | 'skipped' | 'failed';
  summary: string;
  checks: WorkflowCheckDto[];
  actions: WorkflowActionOutcomeDto[];
  error: string | null;
  attempts: number;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export const NOTIFICATION_TYPES = [
  'inquiry_assigned',
  'inquiry_reassigned',
  'follow_up_due',
  'follow_up_overdue',
  'response_overdue',
  'response_escalated',
  'automation_failed',
  'channel_attention',
  'rule_notification',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export interface NotificationDto {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  /** Path inside the workspace, e.g. "inbox/<leadId>", "follow-ups", "channels". */
  link: string | null;
  read: boolean;
  createdAt: string;
}

export interface NotificationListDto {
  items: NotificationDto[];
  unread: number;
  nextBefore: string | null;
}

export const listNotificationsQuerySchema = z.object({
  unreadOnly: z.coerce.boolean().default(false),
  before: z.string().datetime({ offset: true }).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export const markNotificationsSchema = z.object({
  ids: z.array(objectId).max(100).optional(),
  all: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Operational overview
// ---------------------------------------------------------------------------

export const overviewQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30),
  connectionId: objectId.optional(),
  source: z.enum(LEAD_SOURCES).optional(),
  assigneeId: objectId.optional(),
});

export interface OperationsOverviewDto {
  inquiries: {
    total: number;
    returning: number;
    byStatus: { status: string; count: number }[];
    byChannel: { connectionId: string | null; name: string; count: number }[];
  };
  team: {
    /** Inquiries received in the range that are still waiting for a human reply. */
    unanswered: number;
    firstResponse: { medianMinutes: number | null; sampleSize: number; withinThreshold: number | null; thresholdMinutes: number | null };
    agents: {
      userId: string;
      name: string;
      assigned: number;
      awaitingReply: number;
      medianFirstResponseMinutes: number | null;
      overdueFollowUps: number;
      completedFollowUps: number;
    }[];
    followUps: { overdue: number; completed: number; open: number };
  };
  integrations: {
    connected: number;
    needsAttention: number;
    leadImports: { imported: number; failed: number };
    lastActivityAt: string | null;
  };
  automation: { executed: number; skipped: number; failed: number; assignments: number; followUpsCreated: number };
}

export interface AttentionItemDto {
  leadId: string;
  customerName: string;
  source: string;
  status: string;
  assignee: { id: string; name: string } | null;
  awaitingSince: string;
  waitingMinutes: number;
}
